import { AxiosError, AxiosRequestConfig } from 'axios';
import { RestResult, RestResultWithData } from '../commons/types';
import api from '../../index';
import {
  AgestockReportVehicleResponse,
  SlaReportVehicleResponse,
  CustomerVehicleSheetRequest,
  DisapproveVehicleDoubleCheckRequest,
  DisapproveVehicleRequest,
  RequestNewVehicleServices,
  RequestVehicleServiceEdition,
  Vehicle,
  VehicleDoubleCheck,
  VehicleGetRequest,
  VehicleModel,
  VehicleServiceRequestResponse,
  DispatchedVehicles,
  DispatchedMetricsResponse,
  DispatchedSummaryTypeVehicles,
  DispatchedSummaryChartSadaVehicles,
  DispatchedSummaryChartOthersVehicles,
  IResponseDispatchedVehicleTimelineChart,
  MonitoringUpdateVehicles,
  ReportDispatchedVehicles,
  SearchVehicles,
  SearchVehiclesFilter,
} from './types';
import { safeRequest, safeRequestThrowable } from '../commons';
import { DashboardFilter } from '@/src/components/client-select/types';
import { IResponseMetricsChart } from '@/src/pages/operationalMetricsPage/types';
import { FilterDispatchedReport } from '../daily/types';

export const getById = async (id: string, branch: string): Promise<RestResult<Vehicle>> => {
  try {
    const response = await api.instance.get<Vehicle>(`/vehicles-v2/${id}`, {
      params: { branch },
    } as AxiosRequestConfig);

    return {
      statusCode: response.status,
      data: response.data,
    };
  } catch (e) {
    const error = e as AxiosError;
    if (error.isAxiosError) {
      return {
        statusCode: error.response?.status || 0,
        error,
      };
    }
    return {
      statusCode: 0,
      error: e,
    };
  }
};

export const fetchModels = async (chassis: string[]): Promise<RestResult<VehicleModel[]>> => {
  const result = await safeRequestThrowable<VehicleModel[]>({
    method: 'post',
    url: '/global/vehicles',
    data: { chassis },
  });
  return { ...result, data: result.data.data };
};

export const fetchDoubleCheck = async (
  chassis: string[],
): Promise<RestResult<VehicleDoubleCheck>> => {
  const result = await safeRequestThrowable<VehicleDoubleCheck>({
    method: 'post',
    url: '/vehicles-v2/double-check',
    data: { chassis },
  });
  return { ...result, data: result.data.data };
};

export const requestServicesForVehicles = async (
  request: RequestNewVehicleServices,
): Promise<RestResult<VehicleServiceRequestResponse>> => {
  const result = await safeRequestThrowable<VehicleServiceRequestResponse>({
    method: 'post',
    url: '/vehicles-v2',
    data: request,
  });

  return {
    ...result,
    data: result.data?.data,
  };
};

export const requestServicesForMultipleVehicles = async (
  request: CustomerVehicleSheetRequest[],
): Promise<RestResult<VehicleServiceRequestResponse>> => {
  const result = await safeRequestThrowable<VehicleServiceRequestResponse>({
    method: 'post',
    url: '/vehicles-v2/sheet',
    data: request,
  });

  return {
    ...result,
    data: result.data?.data,
  };
};

export const editServiceRequestForVehicle = async (
  vehicleId: string,
  request: RequestVehicleServiceEdition,
): Promise<RestResult<VehicleServiceRequestResponse>> => {
  const result = await safeRequestThrowable<VehicleServiceRequestResponse>({
    method: 'put',
    url: `/vehicles-v2/${vehicleId}`,
    data: request,
  });

  return {
    ...result,
    data: result.data?.data,
  };
};

export const fetchVehicleServiceRequest = async (
  requestId: string,
): Promise<RestResult<VehicleServiceRequestResponse>> => {
  const result = await safeRequest<VehicleServiceRequestResponse>({
    method: 'get',
    url: `/vehicles-v2/${requestId}`,
    data: null,
  });

  return {
    ...result,
    data: result.data?.data,
  };
};

export const deleteVehicleRequest = async (vehicles: string[]): Promise<RestResult<null>> => {
  const result = await safeRequestThrowable<null>({
    method: 'delete',
    url: '/vehicles-v2/',
    data: {
      vehicles,
    },
  });
  return {
    ...result,
    data: result.data?.data,
  };
};

export const sendVehiclesRequest = async (vehicles: string[]): Promise<RestResult<null>> => {
  const result = await safeRequestThrowable<null>({
    method: 'post',
    url: '/vehicles-v2/send',
    data: {
      vehicles,
    },
  });
  return {
    ...result,
    data: result.data?.data,
  };
};

export const bondVehiclesDataSul = async (branch: string): Promise<RestResult<any>> => {
  const result = await safeRequest<null>({
    method: 'post',
    url: '/admin/vehicles-v2/chassis-data-sul',
    data: {
      branch,
    },
  });
  return {
    ...result,
    data: result.data?.data ?? {},
  };
};

export const exportVehiclesAdmin = async (params: any): Promise<RestResult<BlobPart>> => {
  const result = await safeRequest<BlobPart>({
    method: 'get',
    url: '/admin/vehicles-v2/export',
    data: null,
    responseType: 'blob',
    params,
  });
  return {
    ...result,
    data: result.data?.data,
  };
};

export const getVehicles = async (filter?: Partial<VehicleGetRequest>) => {
  const result = await safeRequestThrowable({
    method: 'get',
    url: '/admin/vehicles-v2',
    params: filter,
    data: null,
  });

  return {
    ...result,
    data: result.data.data,
  };
};

export const exportVehicles = async (params: any): Promise<RestResult<BlobPart>> => {
  const result = await safeRequest<BlobPart>({
    method: 'get',
    url: '/vehicles-v2/export',
    data: null,
    responseType: 'blob',
    params,
  });
  return {
    ...result,
    data: result.data?.data,
  };
};

export const disapproveVehicles = async (filter: DisapproveVehicleRequest) => {
  const result = await safeRequestThrowable({
    method: 'post',
    data: filter,
    url: '/admin/vehicles-v2/disapprove',
  });

  return {
    data: result.data?.data,
  };
};

export const disapproveVehiclesDoubleCheck = async (
  filter: DisapproveVehicleDoubleCheckRequest,
) => {
  const result = await safeRequestThrowable({
    method: 'post',
    data: filter,
    url: '/admin/vehicles-v2/disapprove/double-check',
  });

  return {
    data: result.data?.data,
  };
};

export const updateVehicle = async (id: string): Promise<void> => {
  await safeRequestThrowable({ method: 'get', url: `/vehicles-v2/${id}/update-os` });
};

export const fetchAgestockReportVehicles = async (
  filter: DashboardFilter,
): Promise<RestResultWithData<AgestockReportVehicleResponse[]>> => {
  const result = await safeRequestThrowable<AgestockReportVehicleResponse[]>({
    method: 'post',
    url: `/vehicles-v2/daily/agestock-report`,
    data: {
      dealerships: filter.selectedDealerships,
      branch: filter.branch,
      rentals: filter.selectedRentals,
      pendency: filter.pendency,
      daysReceiptNfStart: filter.daysReceiptNfStart,
      daysReceiptNfEnd: filter.daysReceiptNfEnd,
      search: filter.search,
    },
  });
  return { ...result, data: result.data.data };
};

export const fetchAdminAgestockReportVehicles = async (
  filter: DashboardFilter,
): Promise<RestResultWithData<AgestockReportVehicleResponse[]>> => {
  const result = await safeRequestThrowable<AgestockReportVehicleResponse[]>({
    method: 'post',
    url: `/admin/vehicles-v2/daily/agestock-report`,
    data: {
      dealerships: filter.selectedDealerships,
      branch: filter.branch,
      rentals: filter.selectedRentals,
      pendency: filter.pendency,
      daysReceiptNfStart: filter.daysReceiptNfStart,
      daysReceiptNfEnd: filter.daysReceiptNfEnd,
      search: filter.search,
    },
  });
  return { ...result, data: result.data.data };
};

export const fetchSlaReportVehicles = async (
  filter: DashboardFilter,
): Promise<RestResultWithData<SlaReportVehicleResponse[]>> => {
  const result = await safeRequestThrowable<SlaReportVehicleResponse[]>({
    method: 'post',
    url: `/vehicles-v2/daily/sla-report`,
    data: {
      dealerships: filter.selectedDealerships,
      branch: filter.branch,
      rentals: filter.selectedRentals
    },
  });
  return { ...result, data: result.data.data };
};

export const fetchAdminSlaReportVehicles = async (
  filter: DashboardFilter,
): Promise<RestResultWithData<SlaReportVehicleResponse[]>> => {
  const result = await safeRequestThrowable<SlaReportVehicleResponse[]>({
    method: 'post',
    url: `/admin/vehicles-v2/daily/sla-report`,
    data: {
      dealerships: filter.selectedDealerships,
      branch: filter.branch,
      rentals: filter.selectedRentals,
      pendency: filter.pendency
    },
  });
  return { ...result, data: result.data.data };
};


export const fetchSummaryDispatched = async (
  filter: DashboardFilter,
): Promise<RestResultWithData<DispatchedMetricsResponse>> => {
  const result = await safeRequestThrowable<DispatchedMetricsResponse>({
    method: 'post',
    url: `/vehicles-v2/dispatched/summary`,
    data: {
      dealerships: filter.selectedDealerships,
      branch: filter.branch,
      rentals: filter.selectedRentals
    },
  });
  return { ...result, data: result.data.data };
};

export const fetchAdminSummaryDispatched = async (
  filter: DashboardFilter,
): Promise<RestResultWithData<DispatchedMetricsResponse>> => {
  const result = await safeRequestThrowable<DispatchedMetricsResponse>({
    method: 'post',
    url: `/admin/vehicles-v2/dispatched/summary`,
    data: {
      dealerships: filter.selectedDealerships,
      branch: filter.branch,
      rentals: filter.selectedRentals
    },
  });
  return { ...result, data: result.data.data };
};

export const fetchDispatchedVehicles = async (
  filter: DashboardFilter,
): Promise<RestResultWithData<DispatchedVehicles>> => {
  const result = await safeRequestThrowable<DispatchedVehicles>({
    method: 'post',
    url: `/vehicles-v2/dispatched/vehicles`,
    data: {
      dealerships: filter.selectedDealerships,
      branch: filter.branch,
      rentals: filter.selectedRentals,
      page: filter.page,
      perPage: filter.perPage,
      statusFilter: filter.statusFilter,
      chassiOsFilter: filter.chassiOsFilter
    },
  });
  return { ...result, data: result.data.data };
};

export const fetchAdminDispatchedVehicles = async (
  filter: DashboardFilter,
): Promise<RestResultWithData<DispatchedVehicles>> => {
  const result = await safeRequestThrowable<DispatchedVehicles>({
    method: 'post',
    url: `/admin/vehicles-v2/dispatched/vehicles`,
    data: {
      dealerships: filter.selectedDealerships,
      branch: filter.branch,
      rentals: filter.selectedRentals,
      page: filter.page,
      perPage: filter.perPage,
      statusFilter: filter.statusFilter,
      chassiOsFilter: filter.chassiOsFilter
    },
  });
  return { ...result, data: result.data.data };
};

export const fetchAdminDispatchedSummaryAgestockVehicles = async (
  filter: DashboardFilter,
): Promise<RestResultWithData<DispatchedSummaryTypeVehicles[]>> => {
  const result = await safeRequestThrowable<DispatchedSummaryTypeVehicles[]>({
    method: 'post',
    url: `/admin/vehicles-v2/dispatched/summary/agestock`,
    data: {
      dealerships: filter.selectedDealerships,
      branch: filter.branch,
      rentals: filter.selectedRentals,
      page: filter.page,
      perPage: filter.perPage,
      statusFilter: filter.statusFilter,
      chassiOsFilter: filter.chassiOsFilter
    },
  });
  return { ...result, data: result.data.data };
};

export const fetchDispatchedSummaryAgestockVehicles = async (
  filter: DashboardFilter,
): Promise<RestResultWithData<DispatchedSummaryTypeVehicles[]>> => {
  const result = await safeRequestThrowable<DispatchedSummaryTypeVehicles[]>({
    method: 'post',
    url: `/vehicles-v2/dispatched/summary/agestock`,
    data: {
      dealerships: filter.selectedDealerships,
      branch: filter.branch,
      rentals: filter.selectedRentals,
      page: filter.page,
      perPage: filter.perPage,
      statusFilter: filter.statusFilter,
      chassiOsFilter: filter.chassiOsFilter
    },
  });
  return { ...result, data: result.data.data };
};

export const fetchDispatchedSummaryChartSadaVehicles = async (
  filter: DashboardFilter,
): Promise<RestResultWithData<DispatchedSummaryChartSadaVehicles>> => {
  const result = await safeRequestThrowable<DispatchedSummaryChartSadaVehicles>({
    method: 'post',
    url: `/vehicles-v2/dispatched/summary/chart-sada`,
    data: {
      dealerships: filter.selectedDealerships,
      branch: filter.branch,
      rentals: filter.selectedRentals,
      page: filter.page,
      perPage: filter.perPage,
      statusFilter: filter.statusFilter,
      chassiOsFilter: filter.chassiOsFilter
    },
  });
  return { ...result, data: result.data.data };
};

export const fetchAdminDispatchedSummaryChartSadaVehicles = async (
  filter: DashboardFilter,
): Promise<RestResultWithData<DispatchedSummaryChartSadaVehicles>> => {
  const result = await safeRequestThrowable<DispatchedSummaryChartSadaVehicles>({
    method: 'post',
    url: `/admin/vehicles-v2/dispatched/summary/chart-sada`,
    data: {
      dealerships: filter.selectedDealerships,
      branch: filter.branch,
      rentals: filter.selectedRentals,
      page: filter.page,
      perPage: filter.perPage,
      statusFilter: filter.statusFilter,
      chassiOsFilter: filter.chassiOsFilter
    },
  });
  return { ...result, data: result.data.data };
};

export const fetchDispatchedSummaryChartOthersVehicles = async (
  filter: DashboardFilter,
): Promise<RestResultWithData<DispatchedSummaryChartOthersVehicles>> => {
  const result = await safeRequestThrowable<DispatchedSummaryChartOthersVehicles>({
    method: 'post',
    url: `/vehicles-v2/dispatched/summary/chart-others-carriers`,
    data: {
      dealerships: filter.selectedDealerships,
      branch: filter.branch,
      rentals: filter.selectedRentals,
      page: filter.page,
      perPage: filter.perPage,
      statusFilter: filter.statusFilter,
      chassiOsFilter: filter.chassiOsFilter
    },
  });
  return { ...result, data: result.data.data };
};

export const fetchAdminDispatchedSummaryChartOthersVehicles = async (
  filter: DashboardFilter,
): Promise<RestResultWithData<DispatchedSummaryChartOthersVehicles>> => {
  const result = await safeRequestThrowable<DispatchedSummaryChartOthersVehicles>({
    method: 'post',
    url: `/admin/vehicles-v2/dispatched/summary/chart-others-carriers`,
    data: {
      dealerships: filter.selectedDealerships,
      branch: filter.branch,
      rentals: filter.selectedRentals,
      page: filter.page,
      perPage: filter.perPage,
      statusFilter: filter.statusFilter,
      chassiOsFilter: filter.chassiOsFilter
    },
  });
  return { ...result, data: result.data.data };
};

export const downloadDispatchedVehiclesLastMileTrack = async (
  filter: DashboardFilter,
): Promise<RestResultWithData<BlobPart>> => {
  const result = await safeRequestThrowable<BlobPart>({
    method: 'post',
    url: `/vehicles-v2/dispatched/vehicles/download`,
    data: {
      dealerships: filter.selectedDealerships,
      branch: filter.branch,
      rentals: filter.selectedRentals,
      statusFilter: filter.statusFilter,
      chassiOsFilter: filter.chassiOsFilter
    },
    responseType: 'blob',
  });
  return { ...result, data: result.data.data };
};

export const downloadAdminDispatchedVehiclesLastMileTrack = async (
  filter: DashboardFilter,
): Promise<RestResultWithData<BlobPart>> => {
  const result = await safeRequestThrowable<BlobPart>({
    method: 'post',
    url: `/admin/vehicles-v2/dispatched/vehicles/download`,
    data: {
      dealerships: filter.selectedDealerships,
      branch: filter.branch,
      rentals: filter.selectedRentals,
      statusFilter: filter.statusFilter,
      chassiOsFilter: filter.chassiOsFilter
    },
    responseType: 'blob',
  });
  return { ...result, data: result.data.data };
};

export const fetchAdminDispatchedVehiclesTimelineChart = async (
  id: string,
): Promise<RestResultWithData<IResponseDispatchedVehicleTimelineChart[]>> => {
  const result = await safeRequestThrowable<IResponseDispatchedVehicleTimelineChart[]>({
    method: 'get',
    url: `/admin/vehicles-v2/dispatched/vehicles/timeline-chart/${id}`,
    data: null
  });
  return { ...result, data: result.data.data };
};


export const fetchDispatchedVehiclesTimelineChart = async (
  id: string,
): Promise<RestResultWithData<IResponseDispatchedVehicleTimelineChart[]>> => {
  const result = await safeRequestThrowable<IResponseDispatchedVehicleTimelineChart[]>({
    method: 'get',
    url: `/vehicles-v2/dispatched/vehicles/timeline-chart/${id}`,
    data: null
  });
  return { ...result, data: result.data.data };
};

export const fetchMetricsVehicleData = async (
  filter: DashboardFilter,
): Promise<RestResultWithData<IResponseMetricsChart>> => {
  const result = await safeRequestThrowable<IResponseMetricsChart>({
    method: 'post',
    url: `/vehicles-v2/metrics`,
    data: {
      dealerships: filter.selectedDealerships,
      branch: filter.branch,
      rentals: filter.selectedRentals,
      year: filter.selectedYear,
      months: filter.selectedMonths
    },
  });
  return { ...result, data: result.data.data };
};

export const fetchAdminMetricsVehicleData = async (
  filter: DashboardFilter,
): Promise<RestResultWithData<IResponseMetricsChart>> => {
  const result = await safeRequestThrowable<IResponseMetricsChart>({
    method: 'post',
    url: `/admin/vehicles-v2/metrics`,
    data: {
      dealerships: filter.selectedDealerships,
      branch: filter.branch,
      rentals: filter.selectedRentals,
      year: filter.selectedYear,
      months: filter.selectedMonths
    },
  });
  return { ...result, data: result.data.data };
};

export const fetchDownloadMetricsVehicleData = async (
  filter: DashboardFilter,
): Promise<RestResultWithData<BlobPart>> => {
  const result = await safeRequestThrowable<BlobPart>({
    method: 'post',
    url: `/vehicles-v2/metrics/download`,
    data: {
      dealerships: filter.selectedDealerships,
      branch: filter.branch,
      rentals: filter.selectedRentals,
      statusFilter: filter.statusFilter,
      chassiOsFilter: filter.chassiOsFilter,
      year: filter.selectedYear,
      months: filter.selectedMonths
    },
    responseType: 'blob',
  });
  return { ...result, data: result.data.data };
};

export const fetchAdminDownloadMetricsVehicleData = async (
  filter: DashboardFilter,
): Promise<RestResultWithData<BlobPart>> => {
  const result = await safeRequestThrowable<BlobPart>({
    method: 'post',
    url: `/admin/vehicles-v2/metrics/download`,
    data: {
      dealerships: filter.selectedDealerships,
      branch: filter.branch,
      rentals: filter.selectedRentals,
      statusFilter: filter.statusFilter,
      chassiOsFilter: filter.chassiOsFilter,
      year: filter.selectedYear,
      months: filter.selectedMonths
    },
    responseType: 'blob',
  });
  return { ...result, data: result.data.data };
};

export const fetchMonitoringUpdateVehicles = async (
  filter: DashboardFilter,
): Promise<RestResultWithData<MonitoringUpdateVehicles>> => {
  const result = await safeRequestThrowable<MonitoringUpdateVehicles>({
    method: 'post',
    url: `/admin/vehicles-v2/monitoring-update/vehicles`,
    data: {
      dealerships: filter.selectedDealerships,
      branch: filter.branch,
      rentals: filter.selectedRentals,
      page: filter.page,
      perPage: filter.perPage,
      chassiOsFilter: filter.chassiOsFilter
    },
  });
  return { ...result, data: result.data.data };
};

export const requestVehiclesReportDispatchedAsync = async (
  filter: FilterDispatchedReport
): Promise<RestResultWithData<void>> => {
  const result = await safeRequestThrowable<void>({
    method: 'post',
    url: `/vehicles-v2/report-dispatched/request`,
    data: { ...filter },
    timeout: 10 * 60 * 1000
  });
  return { ...result, data: undefined };
};

export const fetchReportDispatchedVehicles = async (): Promise<RestResultWithData<ReportDispatchedVehicles[]>> => {
  const result = await safeRequestThrowable<ReportDispatchedVehicles[]>({
    method: 'get',
    url: `/vehicles-v2/report-dispatched/list`,
    data: null
  });
  return { ...result, data: result.data.data };
};

export const fetchReportDispatchedVehiclesDownloadItem = async (id: string): Promise<RestResultWithData<BlobPart>> => {
  const result = await safeRequestThrowable<BlobPart>({
    method: 'get',
    url: `/vehicles-v2/report-dispatched/${id}/download`,
    responseType: 'blob'
  });
  return { ...result, data: result.data.data };
};

export const fetchSearchVehicles = async (
  filter: SearchVehiclesFilter,
): Promise<RestResultWithData<SearchVehicles>> => {
  const {
    branch = '',
    page = 1,
    perPage = 10,
    search = '',
    type = null
  } = filter;

  const result = await safeRequestThrowable<SearchVehicles>({
    method: 'post',
    url: `/vehicles-v2/search`,
    data: {
      search: typeof search === 'string' ? search : '',
      page: Number(page) || 1,
      perPage: Number(perPage) || 10,
      branch,
      type: type || null
    },
  });
  return { ...result, data: result.data.data };
};

export const fetchAdminSearchVehicles = async (
  filter: SearchVehiclesFilter,
): Promise<RestResultWithData<SearchVehicles>> => {
  const {
    branch = '',
    page = 1,
    perPage = 10,
    search = '',
    type = null
  } = filter;

  const result = await safeRequestThrowable<SearchVehicles>({
    method: 'post',
    url: `/admin/vehicles-v2/search`,
    data: {
      search: typeof search === 'string' ? search : '',
      page: Number(page) || 1,
      perPage: Number(perPage) || 10,
      branch: branch,
      type: type || null
    },
  });
  return { ...result, data: result.data.data };
};

export const fetchSearchVehiclesExport = async (
  filter: SearchVehiclesFilter,
): Promise<RestResultWithData<BlobPart>> => {
  const {
    branch = '',
    search = '',
    type = null
  } = filter;

  const result = await safeRequestThrowable<BlobPart>({
    method: 'post',
    url: `/vehicles-v2/search/export`,
    data: {
      search: typeof search === 'string' ? search : '',
      branch: branch,
      type: type || null
    },
    responseType: 'blob',
  });
  return { ...result, data: result.data.data };
};

export const fetchAdminSearchVehiclesExport = async (
  filter: SearchVehiclesFilter,
): Promise<RestResultWithData<BlobPart>> => {
  const {
    branch = '',
    search = '',
    type = null
  } = filter;

  const result = await safeRequestThrowable<BlobPart>({
    method: 'post',
    url: `/admin/vehicles-v2/search/export`,
    data: {
      search: typeof search === 'string' ? search : '',
      branch: branch,
      type: type || null
    },
    responseType: 'blob',
  });
  return { ...result, data: result.data.data };
};